<template>
	<div class="filter-chip-button-container d-flex">
		<div
			v-if="!isSearchOpen"
			class="scrollable-wrappers"
			:style="{ width: sortButtonContainerWidth }"
		>
			<div class="button_filter_container" v-if="needScroll && canScrollLeft">
				<v-btn icon class="scroll-button" @click="moveLeft" color="#002855">
					<Icon type="chevron_left" :size="14" color="#002855" />
				</v-btn>
			</div>
			<div class="scrollable-container" ref="scrollContainer">
				<div
					class="scrollable-content"
					:style="{
						transform: `translateX(${translateX}px)`,
						paddingRight: `${rightPadding}px`,
					}"
				>
					<v-btn
						v-for="chip in otherChips"
						:key="getChipName(chip)"
						type="button"
						class="filter-chip-button"
						:class="{ 'filter-chip-button-active': isButtonActive(getChipName(chip)) }"
						@click="handleChipClick(getChipName(chip), getChipLabel(chip))"
					>
						<v-icon
							v-if="
								isButtonActive(getChipName(chip)) && getChipName(chip) !== 'clear'
							"
							color="white"
							@click.stop="clearFilter(getChipName(chip))"
						>
							mdi-close
						</v-icon>
						{{ getChipLabel(chip) }}
					</v-btn>
					<div v-if="sortChip && isSmallScreen" class="sort-button-container">
						<button
							ref="sort"
							type="button"
							class="sort-button"
							:class="{ 'sort-button-active': isButtonActive(sortChip.name) }"
							@click="handleChipClick(sortChip.name, sortChip.content)"
						>
							<Icon type="sort" color="var(--chip-text-normal)" :size="20" />
						</button>
					</div>
				</div>
			</div>
			<div class="button_filter_container_left" v-if="needScroll && canScrollRight">
				<v-btn icon class="scroll-button" @click="moveRight">
					<Icon type="chevron_right" :size="14" color="#002855" />
				</v-btn>
			</div>
		</div>
		<AppSearch v-if="isSearchOpen && showOnThisPage" @closeSearch="closeSearch" />
		<div
			v-if="!isSmallScreen && !isSearchOpen && (showOnThisPage || sortChip)"
			class="sort-button-container"
		>
			<button
				v-if="showOnThisPage"
				type="button"
				class="sort-button"
				:class="{ 'sort-button-active': hasSearchText }"
				@click="openSearch"
			>
				<Icon type="search" color="var(--chip-text-normal)" :size="24" />
			</button>

			<button
				v-if="sortChip"
				ref="sort"
				type="button"
				class="sort-button"
				:class="{ 'sort-button-active': isButtonActive(sortChip.name) }"
				@click="handleChipClick(sortChip.name, sortChip.content)"
			>
				<Icon type="sort" color="var(--chip-text-normal)" :size="24" />
			</button>

			<button
				v-if="isAdvancedResources"
				type="button"
				class="sort-button"
				@click="toggleList"
			>
				<Icon v-if="listView" type="grid2" :size="24" color="var(--chip-text-normal)" />
				<Icon v-else type="list" :size="24" color="var(--chip-text-normal)" />
			</button>
		</div>
	</div>
</template>

<script>
import AppSearch from '@/components/Layouts/parts/AppSearch.vue'
import { mapGetters, mapActions } from 'vuex'
import router from '@/router'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'FilterChipButton',
	components: { AppSearch },
	mixins: [Dwh],
	props: {
		listView: Boolean,
		isAdvancedResources: Boolean,
		useContentAsKey: Boolean,
		isButtonActive: { type: Function, required: true },
		chipsList: { type: Array, default: () => [] },
		clearFilter: { type: Function, required: true },
	},

	data() {
		return {
			isSearchOpen: false,
			isSmallScreen: false,
			translateX: 0,
			rightPadding: 0,
			containerW: 0,
			contentW: 0,
		}
	},
	computed: {
		...mapGetters('app', ['search']),
		...mapGetters('auth', ['profile']),
		currentUser() {
			return this.profile
		},

		sortButtonContainerWidth() {
			return !this.isSmallScreen && (this.showOnThisPage || this.sortChip) ? '436px' : '555px'
		},
		hasSearchText() {
			return !!this.search?.trim()
		},

		showOnThisPage() {
			return [
				'events',
				'users',
				'directory',
				'forums',
				'threads',
				'resources',
				'resource',
				'polls-users',
				'event-speakers',
				'attendees',
				'partners',
			].includes(this.$route.name)
		},
		sortChip() {
			return this.chipsList.find((c) => c.name === 'sort')
		},

		otherChips() {
			const hasChapters = (this.currentUser?.chapters || []).some(
				(ch) => ch.hidden_subgroup === 0 && ch.chapter_name !== 'All Users'
			)

			return this.chipsList.filter((chip) => {
				if (chip.name === 'chapters') return hasChapters
				return chip.name !== 'sort'
			})
		},
		maxScrollWidth() {
			return Math.max(this.contentW - this.containerW, 0)
		},
		needScroll() {
			return this.maxScrollWidth > 0
		},
		canScrollRight() {
			return Math.abs(this.translateX) < this.maxScrollWidth
		},
		canScrollLeft() {
			return this.translateX < 0
		},
	},
	mounted() {
		window.addEventListener('resize', this.updateSizes)
		this.$nextTick(this.updateSizes)
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.updateSizes)
		this.setSearch('')
	},
	methods: {
		router() {
			return router
		},
		...mapActions('app', ['setSearch']),

		getChipName(chip) {
			return this.useContentAsKey ? chip.content : chip.name
		},
		getChipLabel(chip) {
			return this.useContentAsKey ? chip.name : chip.content
		},
		handleChipClick(name, label) {
			this.$emit('handleChipClick', { name, label })
			if (this.$route.name === 'directory') this.toggleDirectoryFilter(label)
			if (this.$route.name === 'partners' && name === 'follow') this.togglePartnerFilter(name)
		},
		openSearch() {
			this.isSearchOpen = true
		},
		closeSearch() {
			this.isSearchOpen = false
		},
		toggleList() {
			this.$emit('toggleList')
		},
		moveLeft() {
			const step = 100
			this.translateX = Math.min(0, this.translateX + step)
			this.rightPadding = 0
		},
		moveRight() {
			const step = 100
			this.translateX = Math.max(-this.maxScrollWidth, this.translateX - step)
			this.rightPadding = Math.abs(this.translateX) >= this.maxScrollWidth ? 10 : 0
		},
		updateSizes() {
			this.isSmallScreen = window.innerWidth <= 480

			if (this.$refs.scrollContainer) {
				this.containerW = this.$refs.scrollContainer.offsetWidth
				this.contentW = this.$refs.scrollContainer.scrollWidth
			}
			this.translateX = Math.max(-this.maxScrollWidth, this.translateX)
		},
		toggleDirectoryFilter(label) {
			this.sendTrigger({
				category: 'Content',
				action: 'directory_chip_selected',
				module: 'Directories',
				data: this.$route.query.category_id,
				title: label,
			})
		},
		togglePartnerFilter(name) {
			this.sendTrigger({
				category: 'Event',
				action: 'click_exhibitor_bookmark_filter',
				module: 'Exhibitor Booth',
				title: name,
			})
		},
	},
	watch: {
		'$route.query.thread_menu_id': function (v) {
			if (v) {
				this.setSearch('')
				this.isSearchOpen = false
			}
		},
		'$route.query.category_id': function (v) {
			if (v) {
				this.setSearch('')
				this.isSearchOpen = false
			}
		},
		$route(to, from) {
			if (to.query.advance !== from.query.advance) {
				this.setSearch('')
				this.isSearchOpen = false
			}
			this.translateX = 0
			this.rightPadding = 0
			this.$nextTick(this.updateSizes)
		},
		chipsList() {
			this.$nextTick(this.updateSizes)
		},
	},
}
</script>

<style lang="scss">
.filter-chip-button-container {
	display: flex;
	justify-content: space-between;
	max-width: 555px;
	width: 100%;

	@media (max-width: 480px) {
		justify-content: flex-start;
	}
}
.scrollable-wrappers {
	display: flex;
	align-items: center;
	width: 420px;
	min-width: 0;

	@media (max-width: 480px) {
		width: 100%;
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
	}
}

.scrollable-container {
	overflow: hidden;
	flex: 1 1 auto;
	min-width: 0;

	@media (max-width: 480px) {
		max-width: 350px;
		overflow: visible;
		width: 100% !important;
		display: flex;
		justify-content: center;
	}
}
.scrollable-content::after {
	content: '';
	flex: 0 0 40px;
}

.scrollable-content {
	display: flex;
	gap: 12px;
	transition: transform 0.3s ease, padding-right 0.3s ease;

	@media (max-width: 480px) {
		flex-wrap: wrap;
		transform: none !important;
	}
}

.filter-chip-button {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 52px;
	height: 31px;
	padding: 6px 16px;
	font-size: 16px;
	background: var(--chip-normal-background) !important;
	color: var(--chip-text-normal) !important;
	border: 1px solid #e2e2e2 !important;
	border-radius: 8px;

	.v-btn__content {
		font-family: Inter, sans-serif;
		font-weight: 500;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
		text-transform: none !important;
	}

	&-active {
		padding: 3px 8px 4px 2px !important;
		background: var(--chip-selected-background) !important;
		color: var(--chip-text-selected) !important;
	}
}
.sort-button-container {
	display: flex;
	align-items: center;
	gap: 8px;
}

.sort-button {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px 16px;
	background: var(--chip-normal-background) !important;
	color: var(--chip-text-normal) !important;
	border: 1px solid #e2e2e2;
	border-radius: 8px;
	font-size: 16px;
	font-weight: bold;
	&-active {
		border: 1.5px solid #0055a3;
		box-shadow: 0 0 0 2px #0055a333;
	}
}

.v-btn--is-elevated {
	box-shadow: unset;
}
</style>
